
const Util = require('util');

const libFableServiceProviderBase = require('fable-serviceproviderbase');

/** @typedef {import('fable')} Fable */
/** @typedef {import('headlight-rest-client')} HeadlightRestClient */

/**
 * Manage bridge documents.
 *
 * TODO: Abstract away bridge. Make this an inspection document manager.
 *
 *   * Create missing inspection documents that are needed given its type.
 */
class HeadlightBridgeDocumentManager extends libFableServiceProviderBase
{
	/**
	 * @param {Fable} pFable
	 * @param {any} pOptions
	 * @param {string} pServiceHash
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Fable & { HeadlightRestClient: HeadlightRestClient }} */
		this.fable;

		/** @type {any} */
		this.options;

		this.serviceType = 'BridgeDocumentManager';

		// Check if there is a REST client ... if not make one
		if (!this.fable.hasOwnProperty('HeadlightRestClient'))
		{
			this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));
			if (!this.options.hasOwnProperty('HeadlightAPIURL'))
			{
				this.options.HeadlightAPIURL = '/1.0/';
			}
			this.client = this.fable.serviceManager.instantiateServiceProvider('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });
		}
		else
		{
			this.client = this.fable.HeadlightRestClient;
		}

		/** @type {HeadlightRestClient} */
		this.client;
		this._fieldsToConcat = [ 'Notes' ];
	}

	/**
	 * @private
	 * @param {string|number} fromDocumentID
	 * @param {boolean} [lite=false]
	 * @return {Promise<Array<any>>}
	 */
	async _loadDocumentPolyJoinsFromDocument(fromDocumentID, lite = true)
	{
		const joins = await this._get(`DocumentPolyJoins${lite ? '/LiteExtended/RemoteType,Ordinal' : ''}/FilteredTo/FBV~IDDocument~EQ~${fromDocumentID}~FBV~RemoteType~EQ~Document~FSF~Ordinal~ASC~0~FSF~IDDocumentPolyJoin~ASC~0`);
		for (const join of joins)
		{
			if (lite)
			{
				delete join.Value; // if we want to re-save these, this breaks that
			}
			else
			{
				// these being here slows down saves, and also re-saves the underlying record
				delete join.RemoteRecord;
			}
		}
		if (!lite) // sorting is broken in the API for non-lite requests...
		{
			joins.sort((a, b) =>
			{
				if (a.Ordinal == b.Ordinal)
				{
					return a.IDDocumentPolyJoin - b.IDDocumentPolyJoin;
				}
				return a.Ordinal - b.Ordinal;
			});
		}
		return joins;
	}

	/**
	 * @private
	 * @param {number|string} inspectionID
	 * @return {Promise<Array<any>>}
	 */
	async _loadInspectionDocumentJoins(inspectionID)
	{
		return await this._get(`InspectionDocumentJoins/FilteredTo/FBV~IDInspection~EQ~${inspectionID}~FSF~Ordinal~ASC~0~FSF~IDInspectionDocumentJoin~ASC~0`);
	}

	/**
	 * @private
	 * @param {string|number} fromDocumentID
	 * @param {string|number} toDocumentID
	 * @param {boolean} [lite=false]
	 * @return {Promise<any>}
	 */
	async _loadDocumentPolyJoinToDocument(fromDocumentID, toDocumentID, lite = true)
	{
		const joins = await this._get(`DocumentPolyJoins${lite ? '/LiteExtended/RemoteType,Ordinal' : ''}/FilteredTo/FBV~IDDocument~EQ~${fromDocumentID}~FBV~RemoteType~EQ~Document~FBV~IDRemote~EQ~${toDocumentID}`);
		for (const join of joins)
		{
			if (lite)
			{
				delete join.Value;
			}
			else
			{
				// these being here slows down saves, and also re-saves the underlying record
				delete join.RemoteRecord;
			}
		}
		return joins[0];
	}

	/**
	 * Async wrapper for HTTP get.
	 *
	 * @param {String} uri The URI to get.
	 *
	 * @return {Promise<any>} The result of the get.
	 */
	async _get(uri)
	{
		this.fable.log.info(`GET ${uri}`);
		return Util.promisify(this.client.getJSON.bind(this.client))(uri);
	}

	/**
	 * Async wrapper for HTTP put.
	 *
	 * @param {String} uri The URI to put to.
	 * @param {any} payload The payload to put (as JSON).
	 *
	 * @return {Promise<any>} The result of the put.
	 */
	async _put(uri, payload)
	{
		this.fable.log.info(`PUT ${uri}`, { payload });
		return Util.promisify(this.client.putJSON.bind(this.client))(uri, payload);
	}

	/**
	 * Async wrapper for HTTP post.
	 *
	 * @param {String} uri The URI to post to.
	 * @param {any} payload The payload to post (as JSON).
	 *
	 * @return {Promise<Object>} The result of the post.
	 */
	async _post(uri, payload)
	{
		this.fable.log.info(`POST ${uri}`, { payload });
		return Util.promisify(this.client.postJSON.bind(this.client))(uri, payload);
	}

	/**
	 * Async wrapper for HTTP delete.
	 *
	 * @param {string} entityType The entity type to delete.
	 * @param {string|number} entityID The entity ID to delete.
	 *
	 * @return {Promise<Object>} The result of the delete.
	 */
	async _deleteEntity(entityType, entityID)
	{
		this.fable.log.info(`DELETE ${entityType}:${entityID}`, { entityType, entityID });
		return Util.promisify(this.client.deleteEntity.bind(this.client))(entityType, entityID);
	}

	/**
	 * Create a Bridge inspection.
	 *
	 * @param {Number} bridgeID
	 * @param {any} inspectionPayload - The new inspection record shape.
	 * @param {Boolean} [physicalAssetPassed] - Set to true if a physical asset id was passed instead of a bridge.
	 *
	 * @return {Promise<any>} The inspection record.
	 */
	/**
	 * Create an inspection for a PhysicalAsset. This is the generic entry
	 * point — creates the inspection, links it to the asset, and generates
	 * the initial inspection documents.
	 *
	 * @param {number|string} pIDPhysicalAsset - The PhysicalAsset ID.
	 * @param {Object} pInspectionPayload - The inspection record to create.
	 * @return {Promise<Object>} The created inspection record.
	 */
	async createInspection(pIDPhysicalAsset, pInspectionPayload)
	{
		return this.createBridgeInspection(pIDPhysicalAsset, pInspectionPayload, true);
	}

	/**
	 * Create an inspection for a Bridge. Resolves Bridge → PhysicalAsset
	 * unless physicalAssetPassed is true, in which case bridgeID is treated
	 * as an IDPhysicalAsset directly.
	 *
	 * @deprecated Use createInspection() for new code — it accepts
	 * IDPhysicalAsset directly without the Bridge lookup.
	 *
	 * @param {number|string} bridgeID - Bridge ID or PhysicalAsset ID.
	 * @param {Object} inspectionPayload - The inspection record.
	 * @param {boolean} [physicalAssetPassed] - If true, bridgeID is an IDPhysicalAsset.
	 * @return {Promise<Object>} The created inspection record.
	 */
	async createBridgeInspection(bridgeID, inspectionPayload, physicalAssetPassed)
	{
		const inspection = await this._post('Inspection', inspectionPayload);
		try
		{
			if (physicalAssetPassed)
			{
				// create join to physical asset
				await this._post('InspectionPhysicalAssetJoin',
					{
						IDPhysicalAsset: bridgeID,
						IDInspection: inspection.IDInspection,
					});
				return inspection;
			}
			// look up bridge
			const bridge = await this._get(`Bridge/${bridgeID}`);
			if (!bridge)
			{
				this.log.error(`Bridge with ID ${bridgeID} not found; not associating inspection with bridge / physical asset`);
				return inspection;
			}
			if (!bridge.StructureNumber)
			{
				this.log.error(`Bridge with ID ${bridgeID} has no structure number; not associating inspection with bridge / physical asset`);
				return inspection;
			}
			if (inspectionPayload.StatusDisplay && !inspectionPayload.IDInspectionStatus)
			{
				// attempt to look up by name
				const status = (await this._get(`InspectionStatuss/FilteredTo/FBV~Code~EQ~${inspectionPayload.StatusDisplay}`))[0];
				if (status)
				{
					inspection.IDInspectionStatus = status.IDInspectionStatus;
				}
			}
			// look up physical asset
			const physicalAssetPolyJoin = (await this._get(`PhysicalAssetPolyJoins/FilteredTo/FBV~RemoteType~EQ~Bridge~FBV~IDRemote~EQ~${bridgeID}`))[0];
			if (!physicalAssetPolyJoin)
			{
				this.log.error(`Physical asset association for bridge ID ${bridgeID} not found; not associating inspection with bridge / physical asset`);
				return inspection;
			}
			const physicalAsset = await this._get(`PhysicalAsset/${physicalAssetPolyJoin.IDPhysicalAsset}`);
			if (!physicalAsset)
			{
				this.log.error(`Physical asset with ID ${physicalAssetPolyJoin.IDPhysicalAsset} not found; not associating inspection with bridge / physical asset`);
				return inspection;
			}
			// create join to physical asset
			await this._post('InspectionPhysicalAssetJoin',
				{
					IDPhysicalAsset: physicalAsset.IDPhysicalAsset,
					IDInspection: inspection.IDInspection,
				});
		}
		finally
		{
			try
			{
				await this.createInspectionDocuments(inspection);
			}
			catch (err)
			{
				this.log.error('Failed to create inspection documents', { inspection, err });
			}
		}
		return inspection;
	}

	/**
	 * @param {number|string} inspectionID
	 *
	 * @return {Promise<any>}
	 */
	/**
	 * Get the PhysicalAsset linked to an inspection.
	 *
	 * @param {number|string} pInspectionID - The inspection ID.
	 * @return {Promise<Object|null>} The PhysicalAsset record, or null.
	 */
	async getPhysicalAssetForInspection(pInspectionID)
	{
		const tmpJoins = await this._get(`InspectionPhysicalAssetJoins/FilteredTo/FBV~IDInspection~EQ~${pInspectionID}`);
		if (tmpJoins.length > 0)
		{
			return await this._get(`PhysicalAsset/${tmpJoins[0].IDPhysicalAsset}`);
		}
		return null;
	}

	/**
	 * Get the Bridge linked to an inspection via PhysicalAsset → PolyJoin.
	 *
	 * @deprecated Use getPhysicalAssetForInspection() for generic asset
	 * lookups. This method is Bridge-specific.
	 *
	 * @param {number|string} inspectionID
	 * @return {Promise<Object|null>}
	 */
	async _getBridgeForInspection(inspectionID)
	{
		const tmpPhysicalAsset = await this.getPhysicalAssetForInspection(inspectionID);
		if (tmpPhysicalAsset)
		{
			const tmpPolyJoin = (await this._get(`PhysicalAssetPolyJoins/FilteredTo/FBV~IDPhysicalAsset~EQ~${tmpPhysicalAsset.IDPhysicalAsset}~FBV~RemoteType~EQ~Bridge`))[0];
			if (tmpPolyJoin)
			{
				return await this._get(`Bridge/${tmpPolyJoin.IDRemote}`);
			}
		}
		return null;
	}

	/**
	 * @param {String} dateString
	 * @return {String} formattedDateString
	 */
	addCurrentTimeToDateString(dateString)
	{
		const dateStr = dateString ? dateString : new Date().toISOString();
		const currentDateAndTime = new Date();
		const hours = String(currentDateAndTime.getHours()).padStart(2, '0');
		const minutes = String(currentDateAndTime.getMinutes()).padStart(2, '0');
		const seconds = String(currentDateAndTime.getSeconds()).padStart(2, '0');
		const timeString = `${hours}:${minutes}:${seconds}`;

		const formattedDateString = dateStr.includes('T') ? `${dateStr.split('T')[0]}T${timeString}:000Z` : `${dateStr}T${timeString}:000Z`;
		return formattedDateString;
	}

	/**
	 * Load the list of current inspection documents for an inspection.
	 * @param {number|string} inspectionID
	 * @return {Promise<Array<any>>} The list of current inspection documents.
	 */
	async getListOfCurrentInspectionIDDocuments(inspectionID)
	{
		if (!inspectionID)
		{
			throw new Error('InspectionID is required');
		}
		const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspectionID);
		if (inspectionDocJoins.length === 0)
		{
			this.log.warn(`No inspection documents found for inspection ID ${inspectionID}`);
			return [];
		}
		const inspectionDocs = await this._get(`Documents/FilteredTo/FBL~IDDocument~INN~${inspectionDocJoins.map((j) => j.IDDocument).join(',')}`);
		if (inspectionDocs.length === 0)
		{
			this.log.warn(`No inspection documents found for inspection ID ${inspectionID} with joins ${inspectionDocJoins.map((j) => j.IDDocument).join(',')}`);
			return [];
		}
		// return only the IDDocuments that are not deleted
		const IDDocumentList = inspectionDocs.filter((doc) => !doc.Deleted).map((j) => j.IDDocument);
		return IDDocumentList.length > 0 ? IDDocumentList : [];
	}

	/**
	 * Create missing inspection documents.
	 *
	 * @param {any} inspection
	 * @param {Number} [projectID]
	 *
	 * @return {Promise<Array<any>>} The documents
	 */
	async createInspectionDocuments(inspection, projectID)
	{
		const createdDocs = [];
		const inspectionID = typeof inspection === 'number' && inspection;
		if (inspectionID && inspectionID > 0)
		{
			// support ID for convenience
			inspection = (await this._get(`Inspections/FilteredTo/FBV~IDInspection~EQ~${inspectionID}`))[0];
		}
		if (!inspection)
		{
			throw new Error('Inspection is required');
		}
		if (!inspection.IDInspection)
		{
			this.log.error('Inspection does not have an IDInspection', { inspection });
			return createdDocs;
		}
		if (!inspection.IDInspectionType)
		{
			this.log.error('Inspection does not have an IDInspectionType', { inspection });
			return createdDocs;
		}
		if (!projectID)
		{
			projectID = inspection.IDProject;
		}
		if (!projectID)
		{
			throw new Error('ProjectID is required to create inspection documents; either pass it or set it on the inspection');
		}

		// get expected documents for inspection type
		const rniJoins = await this._get(`InspectionTypeReportNamedInstanceJoins/FilteredTo/FBV~IDInspectionType~EQ~${inspection.IDInspectionType}~FSF~Ordinal~ASC~0~FSF~IDInspectionTypeReportNamedInstanceJoin~ASC~0`);

		// get existing documents for inspection
		const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspection.IDInspection);
		const inspectionDocs = await this._get(`Documents/FilteredTo/FBL~IDDocument~INN~${inspectionDocJoins.map((d) => d.IDDocument).join(',')}`);

		// create missing documents
		const rnis = await this._get(`ReportNamedInstances/FilteredTo/FBL~IDReportNamedInstance~INN~${rniJoins.map((rni) => rni.IDReportNamedInstance).join(',')}`);

		// Index RNIs by ID for lookup, then build missingTypes in rniJoin ordinal order
		const rnisByID = {};
		for (const rni of rnis)
		{
			rnisByID[rni.IDReportNamedInstance] = rni;
		}
		const missingTypes = rniJoins
			.map((join) => rnisByID[join.IDReportNamedInstance])
			.filter((rni) => rni && !inspectionDocs.some((d) => d.DocumentType === rni.Hash));

		const bridge = await this._getBridgeForInspection(inspection.IDInspection);

		const formData = { };
		if (bridge)
		{
			// this lets the form know this is coming from external so it can handle it specially
			formData.InitialIDBridge = String(bridge.IDBridge);
		}
		let dossier;
		for (const rni of missingTypes)
		{
			const inspectionDate = inspection.StartDate || inspection.ScheduledStartDate || new Date().toISOString();
			const inspectionDateWithCurrentTime = this.addCurrentTimeToDateString(inspectionDate);
			const documentPayload =
			{
				IDProject: projectID,
				DocumentType: rni.Hash,
				Name: `${rni.Name} for ${inspection.Name}`,
				DocumentDate: inspectionDateWithCurrentTime,
				Form: 1,
				FormData: Object.assign({}, formData),
			};
			if (rni.Hash === 'Bridge-Inspection')
			{
				documentPayload.FormData.CurrentInspection = { Date: this._formatDate(inspectionDate, 'MM/YYYY') };
			}
			const doc = await this._post('Document', documentPayload);
			if (doc.DocumentType === 'Inspection-Dossier')
			{
				dossier = doc;
			}
			inspectionDocJoins.push(
				{
					IDInspection: inspection.IDInspection,
					IDDocument: doc.IDDocument,
					IncludeOnDossier: 1,
				});
			inspectionDocs.push(doc);
			createdDocs.push(doc);
		}
		// New docs were created in InspectionTypeReportNamedInstanceJoin.Ordinal order;
		// existing inspections keep their user-set order with new docs appended.
		const updatedJoins = await this._saveJoins(inspectionDocJoins);

		if (!dossier)
		{
			dossier = (await this._get(`Documents/Lite/FilteredTo/FBL~IDDocument~INN~${updatedJoins.map((j) => j.IDDocument).join(',')}~FBV~DocumentType~EQ~Inspection-Dossier`))[0];
		}
		if (dossier)
		{
			const previousDPJoins = await this._loadDocumentPolyJoinsFromDocument(dossier.IDDocument);
			for (const join of updatedJoins)
			{
				if (join.IDDocument == dossier.IDDocument)
				{
					continue;
				}
				if (!join.IncludeOnDossier)
				{
					continue;
				}
				if (!previousDPJoins.some((j) => j.RemoteType == 'Document' && j.IDRemote == join.IDDocument))
				{
					previousDPJoins.push({ IDDocument: dossier.IDDocument, IDRemote: join.IDDocument, RemoteType: 'Document', Ordinal: 0 });
				}
			}

			this._sortDocumentPolyJoinsByInspectionJoins(updatedJoins, previousDPJoins);
			await this._saveJoins(previousDPJoins, 'DocumentPolyJoin');
		}
		return createdDocs;
	}

	/**
	 * @typedef {Function} InspectionDocumentsCallback
	 * @param {any} [error] - The error, if any.
	 * @param {Array<any>} [documents] - The documents, if no error.
	 */

	/**
	 * Create missing inspection documents.
	 *
	 * @param {any} inspection
	 * @param {InspectionDocumentsCallback} callback
	 */
	createInspectionDocumentsCallback(inspection, callback)
	{
		const onceCallback = (err, docs) =>
		{
			const cb = callback;
			callback = () =>
			{
			};
			cb(err, docs);
		};
		this.createInspectionDocuments(inspection).then((docs) => onceCallback(null, docs)).catch((err) => onceCallback(err));
	}

	/**
	 * Add a document type to an inspection type.
	 *
	 * @param {Number} inspectionTypeID
	 * @param {Number} reportNamedInstanceID
	 *
	 * @return {Promise<any>} The (existing, or new) join object.
	 */
	async addDocumentTypeToInspectionType(inspectionTypeID, reportNamedInstanceID)
	{
		if (!(inspectionTypeID > 0) || !(reportNamedInstanceID > 0))
		{
			throw new Error('InspectionTypeID and ReportNamedInstanceID are required');
		}
		if (!(await this._get(`InspectionType/${inspectionTypeID}`))) // don't care, just make sure it exists
		{
			throw new Error(`InspectionType with ID ${inspectionTypeID} does not exist`);
		}
		if (!(await this._get(`ReportNamedInstance/${reportNamedInstanceID}`))) // don't care, just make sure it exists
		{
			throw new Error(`ReportNamedInstance with ID ${reportNamedInstanceID} does not exist`);
		}
		const existing = (await this._get(`InspectionTypeReportNamedInstanceJoins/FilteredTo/FBV~IDInspectionType~EQ~${inspectionTypeID}~FBV~IDReportNamedInstance~EQ~${reportNamedInstanceID}~FSF~Ordinal~ASC~0~FSF~IDInspectionTypeReportNamedInstanceJoin~ASC~0`))[0];
		if (existing)
		{
			return existing;
		}
		return await this._post('InspectionTypeReportNamedInstanceJoin',
			{
				IDInspectionType: inspectionTypeID,
				IDReportNamedInstance: reportNamedInstanceID,
			});
	}

	/**
	 * @typedef {Function} AddDocumentTypeToInspectionTypeCallback
	 * @param {any} [error] - The error, if any.
	 * @param {any} [join] - The join object, if no error.
	 */

	/**
	 * Create missing inspection documents.
	 *
	 * @param {Number} inspectionTypeID
	 * @param {Number} reportNamedInstanceID
	 * @param {AddDocumentTypeToInspectionTypeCallback} callback
	 */
	addDocumentTypeToInspectionTypeCallback(inspectionTypeID, reportNamedInstanceID, callback)
	{
		const onceCallback = (err, docs) =>
		{
			const cb = callback;
			callback = () =>
			{
			};
			cb(err, docs);
		};
		this.addDocumentTypeToInspectionType(inspectionTypeID, reportNamedInstanceID).then((docs) => onceCallback(null, docs)).catch((err) => onceCallback(err));
	}

	/**
	 * Set the included bit on a document in an inspection.
	 *
	 * @param {Number} documentID
	 * @param {Number} inspectionID
	 * @param {Boolean} isIncluded
	 */
	async includeDocumentInInspectionDossier(documentID, inspectionID, isIncluded)
	{
		const join = await this._get(`InspectionDocumentJoins/FilteredTo/FBV~IDInspection~EQ~${inspectionID}~FBV~IDDocument~EQ~${documentID}`).then((joins) => joins[0]);
		if (!join)
		{
			throw new Error(`Document ${documentID} is not associated with inspection ${inspectionID}`);
		}
		const newInclude = !!isIncluded ? 1 : 0;
		if (newInclude == join.IncludeOnDossier)
		{
			return join;
		}
		join.IncludeOnDossier = newInclude;
		const inspectionDocumentJoin = await this._put(`InspectionDocumentJoin`, join);
		// get dossier from inspection
		const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspectionID);
		const dossier = await this._get(`Documents/Lite/FilteredTo/FBV~DocumentType~EQ~Inspection-Dossier~FBL~IDDocument~INN~${inspectionDocJoins.map((j) => j.IDDocument).join(',')}`).then((docs) => docs[0]);
		if (!dossier)
		{
			this.log.warn(`Inspection ${inspectionID} does not have an associated dossier document`);
			return inspectionDocumentJoin;
		}
		// ensure it has a document poly join to this document (or not) based on isIncluded
		const existingPolyJoin = await this._loadDocumentPolyJoinToDocument(dossier.IDDocument, documentID);
		if (existingPolyJoin && !isIncluded)
		{
			await this._deleteEntity(`DocumentPolyJoin`, existingPolyJoin.IDDocumentPolyJoin);
		}
		else if (!existingPolyJoin && isIncluded && dossier.IDDocument != documentID)
		{
			const previousDPJoins = await this._loadDocumentPolyJoinsFromDocument(dossier.IDDocument);
			const orderedDPJoins = previousDPJoins.concat([ { IDDocument: dossier.IDDocument, RemoteType: 'Document', IDRemote: documentID } ]);
			this._sortDocumentPolyJoinsByInspectionJoins(inspectionDocJoins, orderedDPJoins);
			await this._saveJoins(orderedDPJoins, 'DocumentPolyJoin');
		}
		return inspectionDocumentJoin;
	}

	/**
	 * @param {number|string} documentID
	 * @param {number|string} inspectionID
	 * @param {boolean} [includeOnDossier=false]
	 */
	async addDocumentToInspection(documentID, inspectionID, includeOnDossier = false)
	{
		const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspectionID);
		const existingJoin = inspectionDocJoins.find((j) => j.IDDocument == documentID);
		let dossier = await this._get(`Documents/Lite/FilteredTo/FBL~IDDocument~INN~${inspectionDocJoins.map((j) => j.IDDocument).join(',')}~FBV~DocumentType~EQ~Inspection-Dossier`).then((docs) => docs[0]);
		const existingPolyJoin = dossier && await this._loadDocumentPolyJoinToDocument(dossier.IDDocument, documentID);
		if (existingJoin && (existingJoin.IncludeOnDossier ? existingPolyJoin : !existingPolyJoin))
		{
			throw new Error(`Document ${documentID} is already associated with inspection ${inspectionID}`);
		}

		let updatedJoins = inspectionDocJoins;
		let join = existingJoin;
		if (!join)
		{
			join = await this._post('InspectionDocumentJoin', { IDInspection: inspectionID, IDDocument: documentID, IncludeOnDossier: includeOnDossier ? 1 : 0 });
			const orderedJoins = inspectionDocJoins.concat([ join ]);
			updatedJoins = await this._saveJoins(orderedJoins);
		}
		if (join.IncludeOnDossier && !existingPolyJoin)
		{
			// decided to not delete joins here since this is an add method
			if (!dossier)
			{
				// handles the case where the document being added is a dossier, so we add existing documents to it
				dossier = await this._get(`Documents/Lite/FilteredTo/FBL~IDDocument~EQ~${documentID}~FBV~DocumentType~EQ~Inspection-Dossier`).then((docs) => docs[0]);
				if (dossier)
				{
					let needToSave = false;
					const polyJoins = await this._loadDocumentPolyJoinsFromDocument(dossier.IDDocument);
					// need to create joins to this dossier?
					for (const join of updatedJoins)
					{
						if (join.IDDocument == dossier.IDDocument || !join.IncludeOnDossier || polyJoins.some((j) => j.IDRemote == join.IDDocument))
						{
							continue;
						}
						needToSave = true;
						polyJoins.push({ IDDocument: dossier.IDDocument, RemoteType: 'Document', IDRemote: join.IDDocument });
					}

					if (needToSave)
					{
						this._sortDocumentPolyJoinsByInspectionJoins(updatedJoins, polyJoins);
						await this._saveJoins(polyJoins, 'DocumentPolyJoin');
					}
				}
			}
			else
			{
				const previousDPJoins = await this._loadDocumentPolyJoinsFromDocument(dossier.IDDocument);
				const orderedDPJoins = previousDPJoins.concat([ { IDDocument: dossier.IDDocument, RemoteType: 'Document', IDRemote: documentID } ]);
				this._sortDocumentPolyJoinsByInspectionJoins(updatedJoins, orderedDPJoins);
				await this._saveJoins(orderedDPJoins, 'DocumentPolyJoin');
			}
		}

		return join;
	}

	/**
	 * @private
	 * @param {Array<any>} inspectionDocJoins
	 * @param {Array<any>} documentPolyJoins
	 */
	_sortDocumentPolyJoinsByInspectionJoins(inspectionDocJoins, documentPolyJoins)
	{
		const joinsByIDDocument = {};
		for (const join of inspectionDocJoins)
		{
			joinsByIDDocument[join.IDDocument] = join;
		}
		documentPolyJoins.sort((a, b) =>
		{
			const joinA = joinsByIDDocument[a.IDRemote];
			const joinB = joinsByIDDocument[b.IDRemote];
			if (!joinA && !joinB)
			{
				return 0;
			}
			if (!joinB)
			{
				return -1;
			}
			if (!joinA)
			{
				return 1;
			}
			return joinA.Ordinal - joinB.Ordinal;
		});
	}

	/**
	 * @param {number|string} inspectionID
	 * @param {number|string} documentID
	 * @param {number} ordinalOffset
	 */
	async rearrangeDocument(inspectionID, documentID, ordinalOffset)
	{
		if (!ordinalOffset)
		{
			return;
		}
		/** @type {Array<any>} */
		const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspectionID);
		const existingJoinIndex = inspectionDocJoins.findIndex((j) => j.IDDocument == documentID);
		if (existingJoinIndex < 0)
		{
			throw new Error(`Document ${documentID} is not associated with inspection ${inspectionID}`);
		}
		if (existingJoinIndex + ordinalOffset < 0 || existingJoinIndex + ordinalOffset >= inspectionDocJoins.length)
		{
			this.log.error(`Document ${documentID} cannot be moved to ordinal ${existingJoinIndex + ordinalOffset} in inspection ${inspectionID}`,
				{ inspectionID, documentID, ordinalOffset, existingJoinIndex });
			return;
		}
		const existingJoin = inspectionDocJoins[existingJoinIndex];
		inspectionDocJoins.splice(existingJoinIndex, 1);
		inspectionDocJoins.splice(existingJoinIndex + ordinalOffset, 0, existingJoin);
		const updatedJoins = await this._saveJoins(inspectionDocJoins);

		const dossier = await this._get(`Documents/Lite/FilteredTo/FBL~IDDocument~INN~${inspectionDocJoins.map((j) => j.IDDocument).join(',')}~FBV~DocumentType~EQ~Inspection-Dossier`).then((docs) => docs[0]);
		const previousDPJoins = await this._loadDocumentPolyJoinsFromDocument(dossier.IDDocument);
		this._sortDocumentPolyJoinsByInspectionJoins(updatedJoins, previousDPJoins);
		await this._saveJoins(previousDPJoins, 'DocumentPolyJoin');
	}

	/**
	 * @param {number|string} documentID
	 * @param {number|string} inspectionID
	 */
	async removeDocumentFromInspection(documentID, inspectionID)
	{
		const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspectionID);
		const existingJoin = inspectionDocJoins.find((j) => j.IDDocument == documentID);
		const dossier = await this._get(`Documents/Lite/FilteredTo/FBL~IDDocument~INN~${inspectionDocJoins.map((j) => j.IDDocument).join(',')}~FBV~DocumentType~EQ~Inspection-Dossier`).then((docs) => docs[0]);
		const existingPolyJoin = dossier && await this._loadDocumentPolyJoinToDocument(dossier.IDDocument, documentID);

		if (!existingJoin && !existingPolyJoin)
		{
			throw new Error(`Document ${documentID} is not associated with inspection ${inspectionID}`);
		}
		if (existingJoin)
		{
			await this._deleteEntity('InspectionDocumentJoin', existingJoin.IDInspectionDocumentJoin);
		}
		if (existingPolyJoin)
		{
			await this._deleteEntity('DocumentPolyJoin', existingPolyJoin.IDDocumentPolyJoin);
		}
	}

	/**
	 * @param {Array<any>} joins
	 * @param {string} [table='InspectionDocumentJoin']
	 * @return {Promise<Array<any>>} the updated joins
	 */
	async _saveJoins(joins, table = 'InspectionDocumentJoin')
	{
		const updatedJoins = [];
		// update join ordinals and create / update records as needed
		for (let i = 0; i < joins.length; i++)
		{
			const join = joins[i];
			const desiredOrdinal = i;
			if (join[`ID${table}`]) // existing record
			{
				if (join.Ordinal == desiredOrdinal)
				{
					updatedJoins.push(join);
					continue;
				}
				join.Ordinal = desiredOrdinal;
				const updated = await this._put(table, join);
				updatedJoins.push(updated);
			}
			else
			{
				join.Ordinal = desiredOrdinal;
				const created = await this._post(table, join);
				updatedJoins.push(created);
			}
		}
		return updatedJoins;
	}

	/**
	 * Reorder the InspectionTypeReportNamedInstanceJoins for an inspection type.
	 *
	 * @param {Number} inspectionTypeID
	 * @param {Array<Number>} orderedJoinIDs - The IDs of the joins in the desired order.
	 *
	 * @return {Promise<Array<any>>} The updated joins.
	 */
	async reorderInspectionTypeDocuments(inspectionTypeID, orderedJoinIDs)
	{
		if (!(inspectionTypeID > 0))
		{
			throw new Error('InspectionTypeID is required');
		}
		if (!Array.isArray(orderedJoinIDs) || orderedJoinIDs.length === 0)
		{
			throw new Error('orderedJoinIDs must be a non-empty array');
		}
		// Verify the inspection type exists
		if (!(await this._get(`InspectionType/${inspectionTypeID}`)))
		{
			throw new Error(`InspectionType with ID ${inspectionTypeID} does not exist`);
		}

		// Bulk load all joins for this inspection type
		const allJoins = await this._get(`InspectionTypeReportNamedInstanceJoins/FilteredTo/FBV~IDInspectionType~EQ~${inspectionTypeID}~FSF~Ordinal~ASC~0~FSF~IDInspectionTypeReportNamedInstanceJoin~ASC~0`);

		// Sniff test: ensure the passed IDs cover all joins (no partial reorders)
		if (orderedJoinIDs.length !== allJoins.length)
		{
			throw new Error(`Expected ${allJoins.length} join IDs for InspectionType ${inspectionTypeID} but received ${orderedJoinIDs.length}; all joins must be included in a reorder`);
		}

		// Index joins by ID for fast lookup
		const joinsByID = {};
		for (const join of allJoins)
		{
			joinsByID[join.IDInspectionTypeReportNamedInstanceJoin] = join;
		}

		// Validate every passed ID exists and belongs to this inspection type
		for (const joinID of orderedJoinIDs)
		{
			if (!joinsByID[joinID])
			{
				throw new Error(`InspectionTypeReportNamedInstanceJoin with ID ${joinID} does not exist for InspectionType ${inspectionTypeID}`);
			}
		}

		// Only update joins whose ordinal actually changed
		const updatedJoins = [];
		for (let i = 0; i < orderedJoinIDs.length; i++)
		{
			const join = joinsByID[orderedJoinIDs[i]];
			if (join.Ordinal !== i)
			{
				join.Ordinal = i;
				const updated = await this._put('InspectionTypeReportNamedInstanceJoin', join);
				updatedJoins.push(updated);
			}
			else
			{
				updatedJoins.push(join);
			}
		}
		return updatedJoins;
	}

	/**
	 * NOTE: stolen from pict DateFormat template instruction
	 * @param {string} dateValue
	 * @param {string} formatString
	 * @return {string}
	 */
	_formatDate(dateValue, formatString)
	{
		let tmpDayJS = this.fable.Dates.dayJS.utc(dateValue);
		try
		{
			// Try to cast the day to be a specific timezone if one is set for the app
			if (this.options.Timezone)
			{
				tmpDayJS = tmpDayJS.tz(this.options.Timezone);
			}
			else
			{
				try
				{
					tmpDayJS = tmpDayJS.tz(this.fable.Dates.dayJS.tz.guess());
				}
				catch (pError)
				{
					this.log.error(`Error guessing dayJS guess() function; dates may be formatted to GMT by default. (${pError.message || pError})`);
				}
			}
		}
		catch
		{
			//this.log.error(`Error casting date passed timezone using tz .. casting to the browser guess which is [${this.fable.Dates.dayJS.tz.guess()}].`);
			// Day.js will try to guess the user's timezone for us
			try
			{
				tmpDayJS = tmpDayJS.tz(this.fable.Dates.dayJS.tz.guess());
			}
			catch (pError)
			{
				this.log.error(`Error guessing dayJS guess() function; dates may be formatted to GMT by default. (${pError.message || pError})`);
			}
		}

		return tmpDayJS.format(formatString);
	}

	/**
	 * @param {Number|String} inspectionID
	 * @param {Number|String} projectID
	 * @param {String} documentType
	 * @param {Number|String} userID
	 * @param {Boolean} [includeOnDossier=true]
	 * @return {Promise<any>}
	 */
	async createInspectionDocumentForUser(inspectionID, projectID, documentType, userID, includeOnDossier = true)
	{
		const user = await this._get(`User/${userID}`);
		if (!user)
		{
			throw new Error(`User with ID ${userID} not found`);
		}
		const inspection = await this._get(`Inspection/${inspectionID}`);
		if (!inspection)
		{
			throw new Error(`Inspection with ID ${inspectionID} not found`);
		}
		const rni = (await this._get(`ReportNamedInstancesByProject/${projectID}/0/100`)).find((rni) => rni.Hash === documentType);
		if (!rni)
		{
			throw new Error(`Failed to resolve report hash ${documentType} in project ${projectID}`);
		}
		const bridge = await this._getBridgeForInspection(inspectionID);
		const documentPayload =
		{
			//TODO: should we put their name in the document name? makes re-assign really funky
			Name: `${rni.Name} for ${inspection.Name}`,
			IDProject: projectID,
			DocumentType: documentType,
			DocumentDate: new Date().toISOString(),
			Form: 1,
			FormData: { },
		};
		const inspectionDate = inspection.StartDate || inspection.ScheduledStartDate;
		if (bridge)
		{
			documentPayload.FormData.InitialIDBridge = bridge.IDBridge;
		}
		if (rni.Hash === 'Bridge-Inspection')
		{
			documentPayload.FormData.CurrentInspection = { Date: this._formatDate(inspectionDate, 'MM/YYYY') };
		}
		//TODO: check if user already has a document of this type?
		const doc = await this._post('Document', documentPayload);
		const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspection.IDInspection);
		const newJoin = await this._post('InspectionDocumentJoin', { IDInspection: inspectionID, IDDocument: doc.IDDocument, IncludeOnDossier: includeOnDossier ? 1 : 0 });
		const orderedJoins = inspectionDocJoins.concat([ newJoin ]);
		const updatedJoins = await this._saveJoins(orderedJoins);

		if (includeOnDossier)
		{
			const dossier = (await this._get(`Documents/Lite/FilteredTo/FBL~IDDocument~INN~${updatedJoins.map((j) => j.IDDocument).join(',')}~FBV~DocumentType~EQ~Inspection-Dossier`))[0];
			if (dossier && dossier.IDDocument != doc.IDDocument)
			{
				const previousDPJoins = await this._loadDocumentPolyJoinsFromDocument(dossier.IDDocument);
				if (!previousDPJoins.some((j) => j.RemoteType == 'Document' && j.IDRemote == doc.IDDocument))
				{
					previousDPJoins.push({ IDDocument: dossier.IDDocument, IDRemote: doc.IDDocument, RemoteType: 'Document' });
				}
				this._sortDocumentPolyJoinsByInspectionJoins(updatedJoins, previousDPJoins);
				await this._saveJoins(previousDPJoins, 'DocumentPolyJoin');
			}
		}

		await this._get(`Permissions/AssignOwner/Document/${doc.IDDocument}/To/User/${userID}`);
		// ensure it has a document poly join to this document (or not) based on isIncluded
		return doc;
	}

	/**
	 * @param {String} field
	 * @param {Set<String>} touchedMergedElementConditions
	 * @return {boolean}
	 */
	_fieldSeen(field, touchedMergedElementConditions)
	{
		if (touchedMergedElementConditions.has(field))
		{
			return true;
		}
		touchedMergedElementConditions.add(field);
		return false;
	}

	/**
	 * NOTE: provisional - we want to make a tool to allow manual merging eventually
	 *
	 * FIXME: The only document type that this currently supports is bridge inspection (element condition) documents.
	 *
	 * @param {Number|String} inspectionID
	 * @param {Number|String} documentType
	 * @param {Array<Number|String>} [documentIDs]
	 * @param {Number|String} [existingMergedDocumentID]
	 * @param {Boolean} [includeOnDossier=true]
	 */
	async mergeDocuments(inspectionID, documentType, documentIDs, existingMergedDocumentID, includeOnDossier = true)
	{
		if (documentType !== 'Bridge-Inspection')
		{
			throw new Error(`Document type ${documentType} is not supported for merging`);
		}
		// load inspection documents of the given type
		/** @type {Array<any>} */
		let inspectionDocs;
		if (Array.isArray(documentIDs))
		{
			inspectionDocs = await this._get(`Documents/FilteredTo/FBL~IDDocument~INN~${documentIDs.join(',')}~FBV~DocumentType~EQ~${documentType}`);
		}
		else
		{
			const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspectionID);
			inspectionDocs = await this._get(`Documents/FilteredTo/FBL~IDDocument~INN~${inspectionDocJoins.map((d) => d.IDDocument).join(',')}~FBV~DocumentType~EQ~${documentType}`);
		}
		if (!inspectionDocs || inspectionDocs.length < 2)
		{
			throw new Error(`Inspection ${inspectionID} does not have at least two documents of type ${documentType}`);
		}
		let existingMergedDoc;
		if (!existingMergedDocumentID)
		{
			existingMergedDoc = inspectionDocs.find((d) => d.FormData?.Meta?.Merge == true);
			if (existingMergedDoc)
			{
				existingMergedDocumentID = existingMergedDoc.IDDocument;
			}
		}
		// sanity check documents
		let docType;
		let projectID;
		for (const doc of inspectionDocs)
		{
			if (!docType)
			{
				docType = doc.DocumentType;
			}
			else if (doc.DocumentType !== docType)
			{
				throw new Error(`Documents are not all of the same type; expected ${docType} but found ${doc.DocumentType} on document ${doc.IDDocument}`);
			}
			if (!projectID)
			{
				projectID = doc.IDProject;
			}
			else if (doc.IDProject != projectID)
			{
				throw new Error(`Documents are not all in the same project; expected ${projectID} but found ${doc.IDProject} on document ${doc.IDDocument}`);
			}
		}
		// merge
		let newDoc;
		let resetConcat = false; // for concatentated fields, should we reset to note duplicate values
		if (existingMergedDocumentID)
		{
			if (!existingMergedDoc)
			{
				existingMergedDoc = inspectionDocs.find((d) => d.IDDocument == existingMergedDocumentID);
			}
			if (existingMergedDoc)
			{
				newDoc = existingMergedDoc;
				inspectionDocs = inspectionDocs.filter((d) => d.IDDocument != existingMergedDocumentID);
				resetConcat = true;
			}
			else
			{
				newDoc = this._get(`Document/${existingMergedDocumentID}`);
			}
		}
		if (!newDoc)
		{
			newDoc = JSON.parse(JSON.stringify(inspectionDocs.shift()));
			newDoc.Name = `${newDoc.Name} (Merged)`;
			delete newDoc.IDDocument;
			delete newDoc.GUIDDocument;
			delete newDoc.CreatingIDUser;
			delete newDoc.CreateDate;
			delete newDoc.UpdatingIDUser;
			delete newDoc.UpdateDate;
			if (!newDoc.FormData)
			{
				newDoc.FormData = { };
			}
			if (!newDoc.FormData.Meta)
			{
				newDoc.FormData.Meta = { };
			}
			newDoc.FormData.Meta.Merge = true;
		}
		if (!Array.isArray(newDoc.Observations))
		{
			newDoc.Observations = [];
		}
		const mergedFormData = newDoc.FormData;
		const touchedMergedElementConditions = new Set();
		for (const doc of inspectionDocs)
		{
			if (Array.isArray(doc.Observations))
			{
				for (const obsID of doc.Observations)
				{
					if (!newDoc.Observations.includes(obsID))
					{
						newDoc.Observations.push(obsID);
					}
				}
			}
			const formData = doc.FormData;
			if (typeof formData.ElementConditionTotalMap === 'object')
			{
				if (!mergedFormData.ElementConditionTotalMap)
				{
					mergedFormData.ElementConditionTotalMap = { };
				}
				for (const elementID in formData.ElementConditionTotalMap)
				{
					if (mergedFormData.ElementConditionTotalMap.hasOwnProperty(elementID))
					{
						const mergedElementCondition = mergedFormData.ElementConditionTotalMap[elementID];
						const elementCondition = formData.ElementConditionTotalMap[elementID];
						mergedElementCondition.CS2 = String((parseInt(mergedElementCondition.CS2) || 0) + (parseInt(elementCondition.CS2) || 0));
						mergedElementCondition.CS3 = String((parseInt(mergedElementCondition.CS3) || 0) + (parseInt(elementCondition.CS3) || 0));
						mergedElementCondition.CS4 = String((parseInt(mergedElementCondition.CS4) || 0) + (parseInt(elementCondition.CS4) || 0));
						if (mergedElementCondition.TotalAmount)
						{
							mergedElementCondition.CS1 = String(parseInt(mergedElementCondition.TotalAmount) -
								(parseInt(mergedElementCondition.CS2) || 0) - (parseInt(mergedElementCondition.CS3) || 0) - (parseInt(mergedElementCondition.CS4) || 0));
							mergedElementCondition.CS1Percentage = String((parseInt(mergedElementCondition.CS1) / parseInt(mergedElementCondition.TotalAmount) * 100));
							mergedElementCondition.CS2Percentage = String((parseInt(mergedElementCondition.CS2) / parseInt(mergedElementCondition.TotalAmount) * 100));
							mergedElementCondition.CS3Percentage = String((parseInt(mergedElementCondition.CS3) / parseInt(mergedElementCondition.TotalAmount) * 100));
							mergedElementCondition.CS4Percentage = String((parseInt(mergedElementCondition.CS4) / parseInt(mergedElementCondition.TotalAmount) * 100));
						}

						if (Array.isArray(elementCondition.Defects))
						{
							if (!Array.isArray(mergedElementCondition.Defects))
							{
								mergedElementCondition.Defects = [];
							}
							for (const defect of elementCondition.Defects)
							{
								const existingDefect = mergedElementCondition.Defects.find((d) => d.GUIDPart == defect.GUIDPart);
								if (existingDefect)
								{
									mergedElementCondition.Defects = mergedElementCondition.Defects.filter((d) => d.GUIDPart != defect.GUIDPart);
								}
								mergedElementCondition.Defects.push(defect);
								this._transferDefect(doc, newDoc, defect);
							}
						}
					}
					else
					{
						mergedFormData.ElementConditionTotalMap[elementID] = JSON.parse(JSON.stringify(formData.ElementConditionTotalMap[elementID]));
					}
				}
			}
			if (typeof formData.CurrentInspection === 'object')
			{
				if (!mergedFormData.CurrentInspection)
				{
					mergedFormData.CurrentInspection = { };
				}
				for (const field in formData.CurrentInspection)
				{
					if (field == 'ElementConditions')
					{
						if (!mergedFormData.CurrentInspection.ElementConditions)
						{
							mergedFormData.CurrentInspection.ElementConditions = { };
						}
						// merge notes
						for (const elementID of Object.keys(formData.CurrentInspection.ElementConditions))
						{
							if (!mergedFormData.CurrentInspection.ElementConditions[elementID])
							{
								mergedFormData.CurrentInspection.ElementConditions[elementID] = { };
							}
							if (resetConcat && !this._fieldSeen(`CurrentInspection.ElementConditions.${elementID}.Notes`, touchedMergedElementConditions))
							{
								mergedFormData.CurrentInspection.ElementConditions[elementID].Notes = formData.CurrentInspection.ElementConditions[elementID].Notes;
							}
							else if (typeof formData.CurrentInspection.ElementConditions[elementID].Notes === 'string' && formData.CurrentInspection.ElementConditions[elementID].Notes.length > 0)
							{
								if (!mergedFormData.CurrentInspection.ElementConditions[elementID].Notes)
								{
									mergedFormData.CurrentInspection.ElementConditions[elementID].Notes = formData.CurrentInspection.ElementConditions[elementID].Notes;
								}
								else
								{
									mergedFormData.CurrentInspection.ElementConditions[elementID].Notes += '\n';
									mergedFormData.CurrentInspection.ElementConditions[elementID].Notes += formData.CurrentInspection.ElementConditions[elementID].Notes;
								}
							}
						}
					}
					else if (!mergedFormData.CurrentInspection.hasOwnProperty(field))
					{
						mergedFormData.CurrentInspection[field] = formData.CurrentInspection[field];
					}
					else if (this._fieldsToConcat.includes(field))
					{
						if (resetConcat && !this._fieldSeen(`CurrentInspection.${field}`, touchedMergedElementConditions))
						{
							mergedFormData.CurrentInspection[field] = formData.CurrentInspection[field];
						}
						// merge field - we don't merge all of them, but remarks etc get concatenated
						else if (typeof formData.CurrentInspection[field] === 'string' && formData.CurrentInspection[field].length > 0)
						{
							if (!mergedFormData.CurrentInspection[field])
							{
								mergedFormData.CurrentInspection[field] = formData.CurrentInspection[field];
							}
							else
							{
								mergedFormData.CurrentInspection[field] += '\n';
								mergedFormData.CurrentInspection[field] += formData.CurrentInspection[field];
							}
						}
					}
					else if (!mergedFormData.CurrentInspection[field])
					{
						mergedFormData.CurrentInspection[field] = formData.CurrentInspection[field];
					}
				}
			}
			for (const formDataKey of Object.keys(formData))
			{
				if (!mergedFormData.hasOwnProperty(formDataKey))
				{
					mergedFormData[formDataKey] = formData[formDataKey];
				}
			}
		}

		delete newDoc.FormData.InitialIDBridge; // never want this on a merged doc - can be there if one doc to merge was never opened
		// save as new document
		const savedMergedDoc = await this._put('Document/Upsert', newDoc);
		if (!newDoc.IDDocument)
		{
			const inspectionDocJoins = await this._loadInspectionDocumentJoins(inspectionID);
			const orderedJoins = inspectionDocJoins.concat([ { IDInspection: inspectionID, IDDocument: savedMergedDoc.IDDocument, IncludeOnDossier: includeOnDossier ? 1 : 0 } ]);
			const updatedJoins = await this._saveJoins(orderedJoins);
			if (includeOnDossier)
			{
				const dossier = (await this._get(`Documents/Lite/FilteredTo/FBL~IDDocument~INN~${updatedJoins.map((j) => j.IDDocument).join(',')}~FBV~DocumentType~EQ~Inspection-Dossier`))[0];
				if (dossier)
				{
					const previousDPJoins = await this._loadDocumentPolyJoinsFromDocument(dossier.IDDocument);
					if (!previousDPJoins.some((j) => j.RemoteType == 'Document' && j.IDRemote == savedMergedDoc.IDDocument))
					{
						previousDPJoins.push({ IDDocument: dossier.IDDocument, IDRemote: savedMergedDoc.IDDocument, RemoteType: 'Document' });
						this._sortDocumentPolyJoinsByInspectionJoins(updatedJoins, previousDPJoins);
						await this._saveJoins(previousDPJoins, 'DocumentPolyJoin');
					}
				}
			}
		}
	}

	/**
	 * @param {any} mergedFormData
	 * @param {any} sourceFormData
	 * @param {any} defect
	 */
	_transferDefect(mergedFormData, sourceFormData, defect)
	{
		// observation set binding to defect
		const binding = `${defect.IDDefect}-${defect.GUIDPart}`;
		if (sourceFormData[binding])
		{
			if (mergedFormData[binding] && mergedFormData[binding] != sourceFormData[binding])
			{
				this.fable.log.warn(`Defect binding ${binding} already exists in merged document`, { old: mergedFormData[binding], new: sourceFormData[binding] });
			}
			mergedFormData[binding] = sourceFormData[binding];
		}
	}
}

module.exports = HeadlightBridgeDocumentManager;
